import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usd, fmtDate, StatusPill } from "./affiliateFormat";

const PayoutRow = ({ p, onAction, connectMode }) => {
  const [ref, setRef] = useState("");
  return (
    <div className="py-3 flex flex-col lg:flex-row lg:items-center gap-3" data-testid={`admin-payout-row-${p.payout_id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm">{p.affiliate?.name || p.affiliate_user_id}</p>
        <p className="text-[11px] text-gray-500">{p.affiliate?.email} · requested {fmtDate(p.requested_at)} · Stripe {p.affiliate?.stripe_connect_ready ? "connected" : "not connected"}</p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-bold text-white" data-testid={`admin-payout-amount-${p.payout_id}`}>{usd(p.amount_cents)}</span>
        <StatusPill status={p.status} testId={`admin-payout-status-${p.payout_id}`} />
        {p.method && <span className="text-[11px] text-gray-400">{p.method}{p.reference ? ` · ${p.reference}` : ""}{p.stripe_transfer_id ? ` · ${p.stripe_transfer_id}` : ""}</span>}
        {p.status === "requested" && (
          <>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onAction(p.payout_id, { action: "approve" })} data-testid={`admin-payout-approve-${p.payout_id}`}>Approve</Button>
            <Button size="sm" variant="outline" onClick={() => onAction(p.payout_id, { action: "reject" })} data-testid={`admin-payout-reject-${p.payout_id}`}>Reject</Button>
          </>
        )}
        {p.status === "approved" && (
          <>
            <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Payment reference" className="w-40 h-8 text-xs" data-testid={`admin-payout-ref-input-${p.payout_id}`} />
            <Button size="sm" disabled={!ref.trim()} onClick={() => onAction(p.payout_id, { action: "mark_paid_manual", reference: ref })} data-testid={`admin-payout-mark-paid-${p.payout_id}`}>Mark paid</Button>
            {connectMode === "test" && p.affiliate?.stripe_connect_ready && (
              <Button size="sm" variant="outline" onClick={() => onAction(p.payout_id, { action: "send_stripe_test" })} data-testid={`admin-payout-stripe-test-${p.payout_id}`}>Send Stripe test transfer</Button>
            )}
            <Button size="sm" variant="outline" onClick={() => onAction(p.payout_id, { action: "reject" })} data-testid={`admin-payout-reject-approved-${p.payout_id}`}>Reject</Button>
          </>
        )}
      </div>
    </div>
  );
};

export const PayoutsTable = ({ rows, onAction, connectMode }) => (
  <div className="divide-y divide-white/5" data-testid="admin-payouts-table">
    {rows.length === 0 && <p className="text-sm text-gray-500 py-4" data-testid="admin-payouts-empty">No payout requests yet.</p>}
    {rows.map((p) => <PayoutRow key={p.payout_id} p={p} onAction={onAction} connectMode={connectMode} />)}
  </div>
);

const Field = ({ label, children }) => (
  <label className="block"><span className="text-xs text-gray-400">{label}</span><div className="mt-1">{children}</div></label>
);

export const SettingsForm = ({ settings, onSave }) => {
  const [f, setF] = useState({
    program_enabled: settings.program_enabled, founding_requires_approval: settings.founding_requires_approval,
    commission_pct: Math.round(settings.commission_rate * 100), hold_days: settings.hold_days,
    attribution_days: settings.attribution_days, min_payout: settings.min_payout_cents / 100,
    launch_date: (settings.launch_date || "").slice(0, 10),
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = () => onSave({
    program_enabled: f.program_enabled, founding_requires_approval: f.founding_requires_approval,
    commission_rate: Number(f.commission_pct) / 100, hold_days: Number(f.hold_days), attribution_days: Number(f.attribution_days),
    min_payout_cents: Math.round(Number(f.min_payout) * 100), launch_date: `${f.launch_date}T00:00:00+00:00`,
  });
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-3xl" data-testid="admin-affiliate-settings">
      <label className="flex items-center gap-2 text-sm text-white"><input type="checkbox" checked={f.program_enabled} onChange={set("program_enabled")} data-testid="settings-program-enabled" />Program open</label>
      <label className="flex items-center gap-2 text-sm text-white"><input type="checkbox" checked={f.founding_requires_approval} onChange={set("founding_requires_approval")} data-testid="settings-requires-approval" />Admin approval required</label>
      <div className="text-sm text-rose-200" data-testid="settings-live-payouts-locked">Live payouts: locked (CEO approval)</div>
      <Field label="Commission %"><Input type="number" value={f.commission_pct} onChange={set("commission_pct")} data-testid="settings-commission-pct" /></Field>
      <Field label="Hold days"><Input type="number" value={f.hold_days} onChange={set("hold_days")} data-testid="settings-hold-days" /></Field>
      <Field label="Attribution days"><Input type="number" value={f.attribution_days} onChange={set("attribution_days")} data-testid="settings-attribution-days" /></Field>
      <Field label="Minimum payout ($)"><Input type="number" value={f.min_payout} onChange={set("min_payout")} data-testid="settings-min-payout" /></Field>
      <Field label="Launch date (UTC)"><Input type="date" value={f.launch_date} onChange={set("launch_date")} data-testid="settings-launch-date" /></Field>
      <div className="flex items-end"><Button onClick={save} className="bg-purple-600 hover:bg-purple-700" data-testid="settings-save-btn">Save settings</Button></div>
    </div>
  );
};
